import React, { useState, useEffect } from 'react';
import {
  X,
  Brain,
  BarChart3,
  Heart,
  Target,
  TrendingUp,
  Users,
  AlertTriangle,
  Clock,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Preset evaluation templates
const EVALUATION_PRESETS = {
  comprehensive: {
    name: 'Comprehensive',
    description: 'Full stats + culture analysis',
    statsQuery: 'Analyze offensive and defensive efficiency metrics. Evaluate position-specific skills, shooting percentages, and advanced stats like PER, true shooting %, and usage rate.',
    cultureQuery: 'Assess character, coachability, and team chemistry fit. Evaluate leadership qualities, work ethic, and any transfer history concerns.',
  },
  statsOnly: {
    name: 'Stats Focus',
    description: 'Deep statistical analysis',
    statsQuery: 'Provide detailed statistical breakdown including per-game averages, advanced metrics, efficiency ratings, and position-specific performance indicators.',
    cultureQuery: '',
  },
  cultureOnly: {
    name: 'Culture Focus',
    description: 'Character & fit assessment',
    statsQuery: '',
    cultureQuery: 'Evaluate personality fit, leadership potential, coachability, and team culture alignment. Assess any red flags from transfer history or behavioral patterns.',
  },
  quickEval: {
    name: 'Quick Eval',
    description: 'Brief overview',
    statsQuery: 'Summarize key performance metrics and standout statistics.',
    cultureQuery: 'Brief assessment of character and team fit potential.',
  },
};

// Individual stat analysis options
const STATS_OPTIONS = [
  { id: 'offensive', label: 'Offensive Efficiency', icon: TrendingUp },
  { id: 'defensive', label: 'Defensive Impact', icon: Target },
  { id: 'advanced', label: 'Advanced Metrics', icon: BarChart3 },
  { id: 'shooting', label: 'Shooting Analysis', icon: Sparkles },
];

// Individual culture analysis options
const CULTURE_OPTIONS = [
  { id: 'character', label: 'Character Assessment', icon: Heart },
  { id: 'leadership', label: 'Leadership Qualities', icon: Users },
  { id: 'redflags', label: 'Red Flags Check', icon: AlertTriangle },
  { id: 'urgency', label: 'Recruitment Urgency', icon: Clock },
];

export const MultipleEvaluationQueryModal = ({ isOpen, onClose, evaluateSelectedPlayers, defaultQuery }) => {
  // Split state for two columns
  const [statsQuery, setStatsQuery] = useState('');
  const [cultureQuery, setCultureQuery] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('comprehensive');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedStatsOptions, setSelectedStatsOptions] = useState(['offensive', 'defensive']);
  const [selectedCultureOptions, setSelectedCultureOptions] = useState(['character', 'leadership']);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Initialize from defaultQuery or preset
  useEffect(() => {
    if (defaultQuery) {
      setStatsQuery(defaultQuery);
      setCultureQuery('');
    } else {
      const preset = EVALUATION_PRESETS.comprehensive;
      setStatsQuery(preset.statsQuery);
      setCultureQuery(preset.cultureQuery);
    }
  }, [defaultQuery]);

  // Handle preset selection
  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    const preset = EVALUATION_PRESETS[presetKey];
    setStatsQuery(preset.statsQuery);
    setCultureQuery(preset.cultureQuery);
  };

  // Build final query from both columns
  const buildFinalQuery = () => {
    let query = '';

    if (statsQuery.trim()) {
      query += `## Statistical Analysis\n${statsQuery.trim()}\n\n`;
    }

    if (cultureQuery.trim()) {
      query += `## Culture & Character Assessment\n${cultureQuery.trim()}\n\n`;
    }

    // Add selected quick options if advanced is shown
    if (showAdvanced) {
      const statsLabels = selectedStatsOptions.map(id =>
        STATS_OPTIONS.find(o => o.id === id)?.label
      ).filter(Boolean);

      const cultureLabels = selectedCultureOptions.map(id =>
        CULTURE_OPTIONS.find(o => o.id === id)?.label
      ).filter(Boolean);

      if (statsLabels.length > 0) {
        query += `Focus areas (Stats): ${statsLabels.join(', ')}\n`;
      }
      if (cultureLabels.length > 0) {
        query += `Focus areas (Culture): ${cultureLabels.join(', ')}\n`;
      }
    }

    // Default instruction
    query += '\nEvaluate this player based on their position and available data. Do not highlight missing data; only provide "Data Gaps" at the end. Provide a player fit score.';

    return query.trim();
  };

  const handleSubmit = () => {
    const finalQuery = buildFinalQuery();
    evaluateSelectedPlayers(finalQuery);
  };

  const toggleOption = (optionId, setSelected, selected) => {
    if (selected.includes(optionId)) {
      setSelected(selected.filter(id => id !== optionId));
    } else {
      setSelected([...selected, optionId]);
    }
  };

  if (!isOpen) return null;

  // Styles matching OLED design system
  const panelClass = cn(
    "rounded-[14px] border p-4 flex flex-col h-full",
    "bg-[#111111] border-[#1A1A1A] backdrop-blur-sm"
  );

  const inputClass = cn(
    "w-full px-3 py-2 rounded-lg border text-sm outline-none transition-all",
    "bg-[#0A0A0A] border-[#1A1A1A] text-[#F5F5F5] placeholder-[#555555] focus:border-[#E8B800]/50 focus:ring-1 focus:ring-[#E8B800]/30"
  );

  const chipClass = (isSelected) => cn(
    "px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5",
    isSelected
      ? "bg-[#E8B800]/20 text-[#E8B800] border border-[#E8B800]/30"
      : "bg-[#111111] text-[#999999] border border-[#1A1A1A] hover:border-[rgba(255,255,255,0.1)]"
  );

  // Handle backdrop click to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 cursor-pointer" onClick={handleBackdropClick}>
      <div className={cn(
        "rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col cursor-default",
        "bg-[#0A0A0A] border border-[#1A1A1A]"
      )} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={cn(
          "px-6 py-4 border-b flex items-center justify-between",
          "border-[#1A1A1A] bg-[#0A0A0A]/80"
        )}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#E8B800]/10">
              <Brain className="h-5 w-5 text-[#E8B800]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#F5F5F5]">
                Player Evaluation
              </h3>
              <p className="text-xs text-[#555555]">
                Configure your evaluation criteria
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:bg-[rgba(255,255,255,0.04)] text-[#999999]"
          >
            <X size={20} />
          </button>
        </div>

        {/* Preset Selection */}
        <div className={cn(
          "px-6 py-3 border-b flex items-center gap-2",
          "border-[#1A1A1A] bg-[#0A0A0A]/50"
        )}>
          <span className="text-xs font-medium text-[#555555]">
            Quick Presets:
          </span>
          {Object.entries(EVALUATION_PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => handlePresetChange(key)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                selectedPreset === key
                  ? "bg-[#E8B800]/20 text-[#E8B800] border border-[#E8B800]/30"
                  : "bg-[#111111] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]"
              )}
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
        </div>

        {/* Two Column Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">

            {/* Stats Analysis Column */}
            <div className={panelClass}>
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="h-4 w-4 text-blue-400" />
                <h4 className="text-sm font-semibold text-[#F5F5F5]">
                  Statistical Analysis
                </h4>
              </div>
              <p className="text-xs mb-3 text-[#555555]">
                Define what performance metrics and statistics to evaluate
              </p>
              <textarea
                className={cn(inputClass, "flex-1 min-h-[120px] resize-none")}
                value={statsQuery}
                onChange={(e) => setStatsQuery(e.target.value)}
                placeholder="e.g., Analyze shooting efficiency, defensive metrics, and advanced stats..."
              />

              {showAdvanced && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {STATS_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = selectedStatsOptions.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        onClick={() => toggleOption(option.id, setSelectedStatsOptions, selectedStatsOptions)}
                        className={chipClass(isSelected)}
                      >
                        <Icon size={12} />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Culture Analysis Column */}
            <div className={panelClass}>
              <div className="flex items-center gap-2 mb-3">
                <Heart className="h-4 w-4 text-pink-400" />
                <h4 className="text-sm font-semibold text-[#F5F5F5]">
                  Culture & Character
                </h4>
              </div>
              <p className="text-xs mb-3 text-[#555555]">
                Define character assessment and team fit criteria
              </p>
              <textarea
                className={cn(inputClass, "flex-1 min-h-[120px] resize-none")}
                value={cultureQuery}
                onChange={(e) => setCultureQuery(e.target.value)}
                placeholder="e.g., Evaluate leadership, coachability, and team chemistry fit..."
              />

              {showAdvanced && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {CULTURE_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = selectedCultureOptions.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        onClick={() => toggleOption(option.id, setSelectedCultureOptions, selectedCultureOptions)}
                        className={chipClass(isSelected)}
                      >
                        <Icon size={12} />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Advanced Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="mt-4 w-full py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-2 bg-[#111111] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]"
          >
            {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showAdvanced ? 'Hide Focus Areas' : 'Show Focus Areas'}
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between border-[#1A1A1A] bg-[#0A0A0A]/80">
          <p className="text-xs text-[#555555]">
            The more specific your criteria, the better the evaluation
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-[#111111] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className={cn(
                "px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2",
                "bg-gradient-to-r from-[#E8B800] to-[#C89E00] hover:from-[#F0C800] hover:to-[#E8B800]",
                "text-[#0A0A0A] shadow-lg shadow-[#E8B800]/20"
              )}
            >
              <Brain size={16} />
              Run Evaluation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EvaluationQueryModal = ({ isOpen, onClose, playerId, onSubmit, defaultQuery, isLoading }) => {
  // Split state for two columns
  const [statsQuery, setStatsQuery] = useState('');
  const [cultureQuery, setCultureQuery] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('comprehensive');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedStatsOptions, setSelectedStatsOptions] = useState(['offensive', 'defensive']);
  const [selectedCultureOptions, setSelectedCultureOptions] = useState(['character', 'leadership']);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Initialize from defaultQuery or preset
  useEffect(() => {
    if (defaultQuery) {
      setStatsQuery(defaultQuery);
      setCultureQuery('');
    } else {
      const preset = EVALUATION_PRESETS.comprehensive;
      setStatsQuery(preset.statsQuery);
      setCultureQuery(preset.cultureQuery);
    }
  }, [defaultQuery]);

  // Handle preset selection
  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    const preset = EVALUATION_PRESETS[presetKey];
    setStatsQuery(preset.statsQuery);
    setCultureQuery(preset.cultureQuery);
  };

  // Build final query from both columns
  const buildFinalQuery = () => {
    let query = '';

    if (statsQuery.trim()) {
      query += `## Statistical Analysis\n${statsQuery.trim()}\n\n`;
    }

    if (cultureQuery.trim()) {
      query += `## Culture & Character Assessment\n${cultureQuery.trim()}\n\n`;
    }

    // Add selected quick options if advanced is shown
    if (showAdvanced) {
      const statsLabels = selectedStatsOptions.map(id =>
        STATS_OPTIONS.find(o => o.id === id)?.label
      ).filter(Boolean);

      const cultureLabels = selectedCultureOptions.map(id =>
        CULTURE_OPTIONS.find(o => o.id === id)?.label
      ).filter(Boolean);

      if (statsLabels.length > 0) {
        query += `Focus areas (Stats): ${statsLabels.join(', ')}\n`;
      }
      if (cultureLabels.length > 0) {
        query += `Focus areas (Culture): ${cultureLabels.join(', ')}\n`;
      }
    }

    // Default instruction
    query += '\nEvaluate this player based on their position and available data. Do not highlight missing data; only provide "Data Gaps" at the end. Provide a player fit score.';

    return query.trim();
  };

  const handleSubmit = () => {
    const finalQuery = buildFinalQuery();
    onSubmit(finalQuery);
  };

  const toggleOption = (optionId, setSelected, selected) => {
    if (selected.includes(optionId)) {
      setSelected(selected.filter(id => id !== optionId));
    } else {
      setSelected([...selected, optionId]);
    }
  };

  if (!isOpen) return null;

  // Styles matching OLED design system
  const panelClass = cn(
    "rounded-[14px] border p-4 flex flex-col h-full",
    "bg-[#111111] border-[#1A1A1A] backdrop-blur-sm"
  );

  const inputClass = cn(
    "w-full px-3 py-2 rounded-lg border text-sm outline-none transition-all",
    "bg-[#0A0A0A] border-[#1A1A1A] text-[#F5F5F5] placeholder-[#555555] focus:border-[#E8B800]/50 focus:ring-1 focus:ring-[#E8B800]/30"
  );

  const chipClass = (isSelected) => cn(
    "px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5",
    isSelected
      ? "bg-[#E8B800]/20 text-[#E8B800] border border-[#E8B800]/30"
      : "bg-[#111111] text-[#999999] border border-[#1A1A1A] hover:border-[rgba(255,255,255,0.1)]"
  );

  // Handle backdrop click to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 cursor-pointer" onClick={handleBackdropClick}>
      <div className={cn(
        "rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col cursor-default",
        "bg-[#0A0A0A] border border-[#1A1A1A]"
      )} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between border-[#1A1A1A] bg-[#0A0A0A]/80">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#E8B800]/10">
              <Brain className="h-5 w-5 text-[#E8B800]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#F5F5F5]">
                Player Evaluation
              </h3>
              <p className="text-xs text-[#555555]">
                Configure your evaluation criteria
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={cn(
              "p-2 rounded-lg transition-colors hover:bg-[rgba(255,255,255,0.04)] text-[#999999]",
              isLoading && "opacity-50 cursor-not-allowed"
            )}
          >
            <X size={20} />
          </button>
        </div>

        {/* Preset Selection */}
        <div className="px-6 py-3 border-b flex items-center gap-2 border-[#1A1A1A] bg-[#0A0A0A]/50">
          <span className="text-xs font-medium text-[#555555]">
            Quick Presets:
          </span>
          {Object.entries(EVALUATION_PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => handlePresetChange(key)}
              disabled={isLoading}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                selectedPreset === key
                  ? "bg-[#E8B800]/20 text-[#E8B800] border border-[#E8B800]/30"
                  : "bg-[#111111] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
        </div>

        {/* Two Column Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">

            {/* Stats Analysis Column */}
            <div className={panelClass}>
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="h-4 w-4 text-blue-400" />
                <h4 className="text-sm font-semibold text-[#F5F5F5]">
                  Statistical Analysis
                </h4>
              </div>
              <p className="text-xs mb-3 text-[#555555]">
                Define what performance metrics and statistics to evaluate
              </p>
              <textarea
                className={cn(inputClass, "flex-1 min-h-[120px] resize-none")}
                value={statsQuery}
                onChange={(e) => setStatsQuery(e.target.value)}
                placeholder="e.g., Analyze shooting efficiency, defensive metrics, and advanced stats..."
                disabled={isLoading}
              />

              {showAdvanced && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {STATS_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = selectedStatsOptions.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        onClick={() => toggleOption(option.id, setSelectedStatsOptions, selectedStatsOptions)}
                        className={chipClass(isSelected)}
                        disabled={isLoading}
                      >
                        <Icon size={12} />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Culture Analysis Column */}
            <div className={panelClass}>
              <div className="flex items-center gap-2 mb-3">
                <Heart className="h-4 w-4 text-pink-400" />
                <h4 className="text-sm font-semibold text-[#F5F5F5]">
                  Culture & Character
                </h4>
              </div>
              <p className="text-xs mb-3 text-[#555555]">
                Define character assessment and team fit criteria
              </p>
              <textarea
                className={cn(inputClass, "flex-1 min-h-[120px] resize-none")}
                value={cultureQuery}
                onChange={(e) => setCultureQuery(e.target.value)}
                placeholder="e.g., Evaluate leadership, coachability, and team chemistry fit..."
                disabled={isLoading}
              />

              {showAdvanced && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {CULTURE_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = selectedCultureOptions.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        onClick={() => toggleOption(option.id, setSelectedCultureOptions, selectedCultureOptions)}
                        className={chipClass(isSelected)}
                        disabled={isLoading}
                      >
                        <Icon size={12} />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Advanced Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            disabled={isLoading}
            className={cn(
              "mt-4 w-full py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-2",
              "bg-[#111111] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]",
              isLoading && "opacity-50 cursor-not-allowed"
            )}
          >
            {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showAdvanced ? 'Hide Focus Areas' : 'Show Focus Areas'}
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between border-[#1A1A1A] bg-[#0A0A0A]/80">
          <p className="text-xs text-[#555555]">
            The more specific your criteria, the better the evaluation
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                "bg-[#111111] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className={cn(
                "px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2",
                "bg-gradient-to-r from-[#E8B800] to-[#C89E00] hover:from-[#F0C800] hover:to-[#E8B800]",
                "text-[#0A0A0A] shadow-lg shadow-[#E8B800]/20",
                isLoading && "opacity-70 cursor-not-allowed"
              )}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#0A0A0A]/30 border-t-[#0A0A0A] rounded-full animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Brain size={16} />
                  Run Evaluation
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
